import { Combobox } from '@shared/components/ui/AdvancedFilter/components';
import { DatePicker } from '@shared/components/ui/date-picker/DatePicker';
import { Button } from '@shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shared/components/ui/shadcn/dialog';
import { Label } from '@shared/components/ui/shadcn/label';
import { useToast } from '@shared/hooks/use-toast';
import { AlertCircle, CheckCircle2, File, FileWarning, Loader2, Upload, XCircle } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCouriersDropdown, useUploadCourierFile } from '../../hooks';
import { UploadCourierFileRequest, UploadCourierFileResponse } from '../../models';

interface UploadCourierFileButtonProps {
  isOpen: boolean;
  onClose: () => void;
}

export const UploadCourierFileButton = ({ isOpen, onClose }: UploadCourierFileButtonProps) => {
  const { t } = useTranslation('codCourierFiles');
  const [file, setFile] = useState<File | null>(null);
  const [courierId, setCourierId] = useState('');
  const [fileDate, setFileDate] = useState<Date | undefined>();
  const [dragActive, setDragActive] = useState(false);
  const [uploadResult, setUploadResult] = useState<UploadCourierFileResponse | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadCourierFileAsync, isUploading } = useUploadCourierFile();
  const { toast } = useToast();
  const { couriers, isLoading: isLoadingCouriers } = useCouriersDropdown();

  const handleUpload = async () => {
    if (!file) {
      toast({
        title: t('upload.error.title', 'Error al subir archivo'),
        description: t('upload.error.file_required', 'Debe seleccionar un archivo'),
        variant: 'destructive',
      });
      return;
    }

    if (!courierId) {
      toast({
        title: t('upload.error.title', 'Error al subir archivo'),
        description: t('upload.error.courier_required', 'Debe seleccionar un courier'),
        variant: 'destructive',
      });
      return;
    }

    if (!fileDate) {
      toast({
        title: t('upload.error.title', 'Error al subir archivo'),
        description: t('upload.error.date_required', 'Debe seleccionar una fecha'),
        variant: 'destructive',
      });
      return;
    }

    try {
      // Convertir fecha a formato Y-m-d (YYYY-MM-DD)
      const formattedDate = fileDate?.toLocaleDateString('en-CA'); // Produce formato YYYY-MM-DD

      const data: UploadCourierFileRequest = {
        file,
        courier_id: parseInt(courierId, 10),
        file_date: formattedDate,
      };

      const result = await uploadCourierFileAsync(data);
      setUploadResult(result);
    } catch {
      // El toast de error se muestra en useUploadCourierFile (onError)
    }
  };

  const handleCloseAndReset = () => {
    onClose();
    setFile(null);
    setCourierId('');
    setFileDate(undefined);
    setUploadResult(null);
  };

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFile(null);
      setCourierId('');
      setFileDate(undefined);
      setDragActive(false);
      setUploadResult(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [isOpen]);

  const handleClose = () => {
    if (!isUploading) {
      handleCloseAndReset();
    }
  };

  // Convertir couriers a formato de opciones para Combobox
  const courierOptions = useMemo(
    () =>
      couriers.map((courier) => ({
        label: courier.name,
        value: String(courier.id),
      })),
    [couriers],
  );

  const handleFileSelect = (selectedFile?: File) => {
    if (!selectedFile) return;

    // Validar extensión del archivo
    const validExtensions = ['.csv', '.xls', '.xlsx', '.pdf'];
    const fileExtension = selectedFile.name.toLowerCase().substring(selectedFile.name.lastIndexOf('.'));

    if (!validExtensions.includes(fileExtension)) {
      toast({
        title: t('upload.error.title', 'Error al subir archivo'),
        description: t('upload.error.invalid_format', 'El archivo debe ser CSV, XLS, XLSX o PDF'),
        variant: 'destructive',
      });
      return;
    }

    setFile(selectedFile);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    handleFileSelect(selectedFile);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (isUploading) return;

    const droppedFile = e.dataTransfer.files?.[0];
    handleFileSelect(droppedFile);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (isUploading) return;
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleClickDropzone = () => {
    if (isUploading) return;
    fileInputRef.current?.click();
  };

  // Calcular totales para mostrar resumen
  const totalSkipped = uploadResult
    ? uploadResult.skipped_duplicate + uploadResult.skipped_no_order + uploadResult.skipped_has_advance
    : 0;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        {uploadResult ? (
          // Vista de resultado después de subir
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                {t('upload.result.title', 'Archivo Procesado')}
              </DialogTitle>
              <DialogDescription>
                {t(
                  'upload.result.description',
                  'El archivo se ha procesado correctamente. A continuación se muestra el resumen:',
                )}
              </DialogDescription>
            </DialogHeader>

            <div className="py-4 space-y-4">
              {/* Resumen principal */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-muted/50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-foreground">{uploadResult.total_lines}</p>
                  <p className="text-xs text-muted-foreground">{t('upload.result.total_lines', 'Líneas procesadas')}</p>
                </div>
                <div className="bg-green-500/10 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-green-600">{uploadResult.payments_created}</p>
                  <p className="text-xs text-muted-foreground">
                    {t('upload.result.payments_created', 'Pagos creados')}
                  </p>
                </div>
              </div>

              {/* Detalles de creación */}
              {(uploadResult.payout_items_created > 0 || uploadResult.ledger_entries_created > 0) && (
                <div className="border border-border rounded-lg p-3 space-y-2">
                  <p className="text-sm font-medium text-foreground flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    {t('upload.result.created_title', 'Registros creados')}
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {uploadResult.payout_items_created > 0 && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          {t('upload.result.payout_items', 'Items de pago:')}
                        </span>
                        <span className="font-medium">{uploadResult.payout_items_created}</span>
                      </div>
                    )}
                    {uploadResult.ledger_entries_created > 0 && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          {t('upload.result.ledger_entries', 'Entradas contables:')}
                        </span>
                        <span className="font-medium">{uploadResult.ledger_entries_created}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Detalles de omitidos */}
              {totalSkipped > 0 && (
                <div className="border border-yellow-500/30 bg-yellow-500/5 rounded-lg p-3 space-y-2">
                  <p className="text-sm font-medium text-foreground flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-yellow-500" />
                    {t('upload.result.skipped_title', 'Registros omitidos')} ({totalSkipped})
                  </p>
                  <div className="space-y-1 text-sm">
                    {uploadResult.skipped_duplicate > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground flex items-center gap-1">
                          <FileWarning className="h-3 w-3" />
                          {t('upload.result.skipped_duplicate', 'Duplicados:')}
                        </span>
                        <span className="font-medium text-yellow-600">{uploadResult.skipped_duplicate}</span>
                      </div>
                    )}
                    {uploadResult.skipped_no_order > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground flex items-center gap-1">
                          <XCircle className="h-3 w-3" />
                          {t('upload.result.skipped_no_order', 'Sin orden asociada:')}
                        </span>
                        <span className="font-medium text-yellow-600">{uploadResult.skipped_no_order}</span>
                      </div>
                    )}
                    {uploadResult.skipped_has_advance > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {t('upload.result.skipped_has_advance', 'Con anticipo previo:')}
                        </span>
                        <span className="font-medium text-yellow-600">{uploadResult.skipped_has_advance}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button onClick={handleCloseAndReset}>{t('upload.result.close', 'Cerrar')}</Button>
            </DialogFooter>
          </>
        ) : (
          // Vista de formulario de subida
          <>
            <DialogHeader>
              <DialogTitle>{t('upload.modal_title', 'Subir Archivo de Courier')}</DialogTitle>
              <DialogDescription>
                {t('upload.modal_description', 'Selecciona el archivo CSV/XLS/PDF del courier para procesarlo.')}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Archivo */}
              <div className="space-y-2">
                <Label>{t('upload.file', 'Archivo')}</Label>
                <div
                  onClick={handleClickDropzone}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  className={`
                                        relative flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors
                                        ${
                                          dragActive
                                            ? 'border-primary bg-primary/5'
                                            : 'border-card-border bg-card-bg hover:border-primary/50 hover:bg-accent/5'
                                        }
                                        ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}
                                    `}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xls,.xlsx,.pdf"
                    onChange={handleFileChange}
                    disabled={isUploading}
                    className="hidden"
                  />

                  {file ? (
                    <div className="flex flex-col items-center gap-2 p-4">
                      <File className="h-8 w-8 text-primary" />
                      <div className="text-center">
                        <p className="text-sm font-medium text-foreground">{file.name}</p>
                        <p className="text-xs text-muted-foreground mt-1">{(file.size / 1024).toFixed(2)} KB</p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFile(null);
                          if (fileInputRef.current) {
                            fileInputRef.current.value = '';
                          }
                        }}
                        disabled={isUploading}
                        className="mt-2"
                      >
                        {t('upload.remove_file', 'Eliminar')}
                      </Button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2 p-4">
                      <Upload className={`h-8 w-8 ${dragActive ? 'text-primary' : 'text-muted-foreground'}`} />
                      <div className="text-center">
                        <p className="text-sm font-medium text-foreground">
                          {dragActive
                            ? t('upload.drop_file', 'Suelta el archivo aquí')
                            : t('upload.click_or_drag', 'Haz clic o arrastra un archivo aquí')}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {t('upload.supported_formats', 'CSV, XLS, XLSX, PDF')}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Courier */}
              <div className="space-y-2">
                <Label>{t('upload.courier', 'Courier')}</Label>
                <Combobox
                  options={courierOptions}
                  value={courierId}
                  setValue={setCourierId}
                  placeholder={
                    isLoadingCouriers
                      ? t('upload.loading_couriers', 'Cargando couriers...')
                      : t('upload.select_courier', 'Seleccionar courier')
                  }
                  translations={{
                    notFound: t('upload.no_couriers_found', 'No se encontraron couriers'),
                  }}
                  disabled={isUploading || isLoadingCouriers}
                />
              </div>

              {/* Fecha del archivo */}
              <div className="space-y-2">
                <Label>{t('upload.file_date', 'Fecha del Archivo')}</Label>
                <DatePicker
                  date={fileDate}
                  onDateChange={setFileDate}
                  placeholder="Seleccionar fecha"
                  disabled={isUploading}
                  data-testid="upload-file-date"
                  className="h-[38px] text-sm"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleClose} disabled={isUploading}>
                {t('upload.cancel', 'Cancelar')}
              </Button>
              <Button onClick={handleUpload} disabled={isUploading || !file || !courierId || !fileDate}>
                {isUploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('upload.uploading', 'Subiendo...')}
                  </>
                ) : (
                  t('upload.confirm', 'Subir Archivo')
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
